import * as React from 'react';
import { EventSubscription } from 'fbemitter';
import { Button, DatePicker, Form, Select, Table } from 'antd';
import * as moment from 'moment';

import { ReadOnlyCalendar } from './ReadOnlyCalendar';

import AssignmentStore, {
  fetchAssignmentsInRange,
} from '../stores/AssignmentStore';
import EditorStore from '../stores/EditorStore';
import SessionStore from '../stores/SessionStore';
import { Selection } from '../enums';
import { removeListener } from '../utils';

interface AssignmentsViewState {
  editors: Editor[];
  editorVolumes: number[];

  selectedDate: moment.Moment;
  assignmentsForDate: { [editor: string]: Assignment };
  assignmentFilters: string[];
  selectedEditor?: string;
  hasChangedEditor: boolean;
  editorAssignments?: AssignmentsByDate;
}

export class AssignmentsView extends React.Component<{}, AssignmentsViewState> {
  private assignmentListener: EventSubscription | null = null;
  private editorListener: EventSubscription | null = null;
  private sessionListener: EventSubscription | null = null;
  private today: string = moment().format('YYYY-MM-DD');

  constructor(props: {}) {
    super(props);

    const now = moment();
    const editor = SessionStore.getEmail();

    fetchAssignmentsInRange(
      moment()
        .subtract(31, 'days')
        .format('YYYY-MM-DD'),
      moment()
        .add(31, 'days')
        .format('YYYY-MM-DD')
    );

    this.state = {
      editors: EditorStore.getEditors(),
      editorVolumes: EditorStore.getVolumes(),

      selectedDate: now,
      assignmentsForDate: AssignmentStore.getAssignmentsForDate(this.today),
      assignmentFilters: [],
      selectedEditor: editor,
      hasChangedEditor: false,
      editorAssignments: AssignmentStore.getAssignmentsForEditor(editor),
    };

    this.changeSelectedDate = this.changeSelectedDate.bind(this);
  }

  render() {
    // tslint:disable-next-line
    const filter = (input: string, option: any) => {
      const email = (option.props.value as string).toLowerCase();
      const name = (option.props.children as string).toLowerCase();
      input = input.toLowerCase();
      return name.includes(input) || email.includes(input);
    };

    let todaysAssignment = <div>Assignment not yet available</div>;
    if (
      this.state.editorAssignments !== undefined &&
      this.state.editorAssignments[this.today]
    ) {
      todaysAssignment = assignmentToHTML(
        this.state.editorAssignments[this.today]
      );
    }

    const selections = [];
    const daysInMonth = moment(this.today).daysInMonth();
    for (let i = 0; i < daysInMonth; i++) {
      const day = moment(this.today)
        .date(i + 1)
        .format('YYYY-MM-DD');
      if (
        this.state.editorAssignments !== undefined &&
        this.state.editorAssignments[day] !== undefined &&
        this.state.editorAssignments[day].assignmentType !== 1
      ) {
        selections.push(Selection.Selected);
      } else {
        selections.push(Selection.None);
      }
    }

    let body = this.allEditorsView();

    if (this.state.selectedEditor !== undefined) {
      let bankDays = 0;
      for (let i = 0; i < this.state.editors.length; i++) {
        const e = this.state.editors[i];
        if (e.email === this.state.selectedEditor) {
          bankDays = e.bankDaysLeft;
          break;
        }
      }

      body = (
        <span>
          <div className="buffer">
            <h2>Assignment for {moment(this.today).format('MMM. Do')}</h2>
            {todaysAssignment}
          </div>
          <div className="buffer">
            <h2>Week at a Glance</h2>
            {this.state.editorAssignments === undefined ? (
              'Data not yet available'
            ) : (
              <table style={{ textAlign: 'center', width: '100%' }}>
                <tbody>
                  <tr
                    style={{
                      color: 'white',
                      backgroundColor: 'rgb(24, 144, 255)',
                    }}
                  >
                    {[-3, -2, -1, 0, 1, 2, 3].map((i) => {
                      const formattedDate = moment(this.today)
                        .add(i, 'days')
                        .format('ddd, MMM Do');
                      return (
                        <td key={this.today + ':' + i} style={{ width: '14%' }}>
                          {i === 0 ? (
                            <strong>{formattedDate}</strong>
                          ) : (
                            formattedDate
                          )}
                        </td>
                      );
                    })}
                  </tr>
                  <tr style={{ verticalAlign: 'top' }}>
                    {[-3, -2, -1, 0, 1, 2, 3].map((i) => {
                      const offsetDay = moment(this.today)
                        .add(i, 'days')
                        .format('YYYY-MM-DD');
                      return (
                        <td
                          key={
                            this.today +
                            ':' +
                            i +
                            ':assignment:' +
                            this.state.selectedEditor
                          }
                        >
                          {assignmentToHTML(
                            this.state.editorAssignments![offsetDay]
                          )}
                        </td>
                      );
                    })}
                  </tr>
                </tbody>
              </table>
            )}
          </div>
          <div className="buffer">
            <h2>{moment(this.today).format('MMMM')} at a Glance</h2>
            <ReadOnlyCalendar
              now={moment(this.today)}
              selections={selections}
            />
          </div>
          <div>
            <h3>Total bank days remaining: {bankDays}</h3>
          </div>
        </span>
      );
    }

    return (
      <div>
        <Form className="buffer" layout="inline">
          <Form.Item label="Select Editor">
            <Select
              filterOption={filter}
              showSearch={true}
              style={{ width: 300 }}
              value={this.state.selectedEditor}
              onChange={(value: string) => {
                this.setState({
                  selectedEditor: value,
                  hasChangedEditor: true,
                  editorAssignments: AssignmentStore.getAssignmentsForEditor(
                    value
                  ),
                });
              }}
            >
              {this.state.editorVolumes.map((v: number) => {
                return (
                  <Select.OptGroup key={v} label={'Volume: ' + v}>
                    {this.state.editors.map((e: Editor) => {
                      if (e.volume !== v) {
                        return null;
                      }
                      return (
                        <Select.Option value={e.email} key={e.email}>
                          {e.name}
                        </Select.Option>
                      );
                    })}
                  </Select.OptGroup>
                );
              })}
            </Select>
          </Form.Item>
          <Form.Item>
            <Button
              disabled={this.state.selectedEditor === undefined}
              onClick={() => {
                this.setState({
                  selectedEditor: undefined,
                });
              }}
            >
              Show All
            </Button>
          </Form.Item>
        </Form>
        {body}
      </div>
    );
  }

  componentDidMount() {
    this.assignmentListener = AssignmentStore.addListener(() => {
      // tslint:disable-next-line
      const assignmentUpdate: any = {
        assignmentsForDate: AssignmentStore.getAssignmentsForDate(this.today),
      };
      if (this.state.selectedEditor !== undefined) {
        assignmentUpdate.editorAssignments = AssignmentStore.getAssignmentsForEditor(
          this.state.selectedEditor
        );
      }
      this.setState(assignmentUpdate);
    });

    this.editorListener = EditorStore.addListener(() => {
      this.setState({
        editors: EditorStore.getEditors(),
        editorVolumes: EditorStore.getVolumes(),
      });
    });

    this.sessionListener = SessionStore.addListener(() => {
      if (!this.state.hasChangedEditor) {
        this.setState({
          selectedEditor: SessionStore.getEmail(),
        });
      }
    });
  }

  componentWillUnmount() {
    removeListener(this.assignmentListener);
    removeListener(this.editorListener);
    removeListener(this.sessionListener);
  }

  private allEditorsView() {
    const columns = [
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
      },
      {
        title: 'Assignment',
        key: 'assignment',
        render: (_: string, e: Editor) => {
          return assignmentToHTML(this.state.assignmentsForDate[e.email]);
        },
      },
    ];

    const editorsByVolume = {};
    for (let i = 0; i < this.state.editorVolumes.length; i++) {
      editorsByVolume[this.state.editorVolumes[i]] = [];
    }
    this.state.editors.map((e: Editor) => {
      const vol = e.volume;
      const assignment = this.state.assignmentsForDate[e.email];
      if (
        this.state.assignmentFilters.length === 0 ||
        (assignment !== undefined &&
          (assignment.assignmentName !== undefined
            ? this.state.assignmentFilters.indexOf(
                assignment.assignmentName.trim()
              ) !== -1
            : this.state.assignmentFilters.indexOf(
                assignment.assignment.trim()
              ) !== -1))
      ) {
        editorsByVolume[vol].push(e);
      }
    });

    const assignmentFilterOptions = [
      'Rotopool',
      'Literature Review',
      'S-Read',
      'A1-Read',
      'Bookgrab',
      '1st Subcite',
      '2nd Subcite',
      'Galleys',
      'Pages',
      'Bluebook',
      'Recent Pub',
      'Special Assignment',
      'Holiday',
      'A2-Read',
    ].map((s: string) => <Select.Option key={s}>{s}</Select.Option>);

    return (
      <span>
        <h2>
          Editor Assignments for{' '}
          <DatePicker
            value={this.state.selectedDate}
            onChange={this.changeSelectedDate}
            format={'MMM. Do'}
            allowClear={false}
          />
        </h2>
        <h3 style={{ marginTop: '20px' }}>
          Filter assignments:{' '}
          <Select
            mode="multiple"
            placeholder="All Assignments"
            showSearch={true}
            style={{ width: 300 }}
            value={this.state.assignmentFilters}
            onChange={(value: string[]) => {
              this.setState({
                assignmentFilters: value,
              });
            }}
          >
            {assignmentFilterOptions}
          </Select>
        </h3>
        {this.state.editorVolumes.map((v: number) => {
          return (
            <span key={'volume-' + v}>
              <h3 style={{ marginTop: '20px' }}>Vol. {v}</h3>
              <Table
                dataSource={editorsByVolume[v]}
                columns={columns}
                pagination={false}
                rowKey="email"
                size="middle"
              />
            </span>
          );
        })}
      </span>
    );
  }

  private changeSelectedDate(date: moment.Moment) {
    this.setState({
      selectedDate: date,
      assignmentsForDate: AssignmentStore.getAssignmentsForDate(
        date.format('YYYY-MM-DD')
      ),
    });
  }
}

function assignmentToHTML(a: Assignment | undefined) {
  if (a === undefined || a.assignmentType === 0) {
    return (
      <div>
        <strong>Assignment not yet available</strong>
      </div>
    );
  } else if (a.assignmentType === 1) {
    return (
      <div>
        <strong>Not in pool</strong>
      </div>
    );
  }

  let title = a.assignment;
  if (a.author !== undefined && a.author !== '') {
    title += ' - ' + a.author;
  }

  const dueDate =
    a.dueDate === undefined ? null : <strong>Due date: {a.dueDate}</strong>;
  const part = a.part === undefined ? null : <div>Part {a.part}</div>;
  const meNote =
    a.meNote === undefined ? null : <div>ME's Note: {a.meNote}</div>;

  return (
    <div>
      <h3>{title}</h3>
      {dueDate}
      {part}
      {meNote}
    </div>
  );
}



// WEBPACK FOOTER //
// ./src/components/AssignmentsView.tsx